import { appDarkPalette, appLightPalette } from './themePalette';

export const adminLayoutLightTokens = {
  adminColorScheme: 'light',
  adminLayoutBg: appLightPalette.pageBg,
  // Solid page colour for places a gradient cannot go (box-shadow ring).
  adminSiderBg: '#f1f5f9',
  adminSiderBorder: '#d8e1ec',
  adminSiderShadow: appLightPalette.shadowSidebar,
  adminHeaderBg: 'rgba(255, 255, 255, 0.9)',
  adminHeaderBorder: '#e8edf3',
  adminTextColor: appLightPalette.text,
  adminTextSecondaryColor: appLightPalette.textSecondary,
  adminPrimaryColor: appLightPalette.primary,
  adminPrimarySoftBg: appLightPalette.primarySoftBg,
  adminPrimarySoftBgAlt: appLightPalette.primarySoftBgAlt,
  adminItemTextColor: appLightPalette.itemText,
  adminItemHoverBg: appLightPalette.itemHoverBg,
  adminItemBorderColor: appLightPalette.border,
  adminDropdownBg: appLightPalette.surfaceBg,
  adminDropdownItemHoverBg: '#f1f5f9',
  adminDropdownShadow: appLightPalette.shadowDropdown,
  adminUploadPlaceholderBg: appLightPalette.surfaceBgAlt,
  adminUploadPlaceholderBorder: '#cbd5e1',
  adminUploadPlaceholderIcon: '#94a3b8',
};

export const adminLayoutDarkTokens = {
  adminColorScheme: 'dark',
  adminLayoutBg: appDarkPalette.pageBg,
  adminSiderBg: appDarkPalette.surfaceBg,
  adminSiderBorder: appDarkPalette.borderSoft,
  adminSiderShadow: appDarkPalette.shadowSidebar,
  adminHeaderBg: appDarkPalette.surfaceBg,
  adminHeaderBorder: appDarkPalette.borderSoft,
  adminTextColor: appDarkPalette.text,
  adminTextSecondaryColor: appDarkPalette.textSecondary,
  adminPrimaryColor: appDarkPalette.primary,
  adminPrimarySoftBg: appDarkPalette.primarySoftBg,
  adminPrimarySoftBgAlt: appDarkPalette.primarySoftBgAlt,
  adminItemTextColor: appDarkPalette.itemText,
  adminItemHoverBg: appDarkPalette.itemHoverBg,
  adminItemBorderColor: appDarkPalette.border,
  adminDropdownBg: appDarkPalette.surfaceElevatedBg,
  adminDropdownItemHoverBg: appDarkPalette.itemHoverBg,
  adminDropdownShadow: appDarkPalette.shadowDropdown,
  adminUploadPlaceholderBg: appDarkPalette.surfaceBgAlt,
  adminUploadPlaceholderBorder: 'rgba(148, 163, 184, 0.28)',
  adminUploadPlaceholderIcon: appDarkPalette.textSecondary,
};

export const createAdminLayoutComponentTheme = (tokens) => ({
  Layout: {
    bodyBg: tokens.adminLayoutBg,
    headerBg: tokens.adminHeaderBg,
    siderBg: tokens.adminSiderBg,
    lightSiderBg: tokens.adminSiderBg,
  },
  Menu: {
    itemBg: 'transparent',
    itemColor: tokens.adminItemTextColor,
    itemHoverBg: tokens.adminItemHoverBg,
    itemHoverColor: tokens.adminTextColor,
    itemSelectedBg: tokens.adminPrimarySoftBg,
    itemSelectedColor: tokens.adminPrimaryColor,
    subMenuItemBg: 'transparent',
  },
  Dropdown: {
    colorBgElevated: tokens.adminDropdownBg,
    controlItemBgHover: tokens.adminDropdownItemHoverBg,
  },
});

export const createAdminCardComponentTheme = (palette) => ({
  colorBgContainer: palette.surfaceBg,
  colorBorderSecondary: palette.borderSoft,
  headerBg: 'transparent',
  actionsBg: palette.surfaceBg,
  extraColor: palette.textSecondary,
  boxShadowTertiary:
    '0 0 0 1px rgba(148, 163, 184, 0.24), 0 18px 46px rgba(0, 0, 0, 0.28)',
  boxShadowCard:
    '0 0 0 1px rgba(147, 197, 253, 0.28), 0 22px 54px rgba(0, 0, 0, 0.34)',
});

const getTokenValue = (token, key, fallback) => token?.[key] || fallback;

export const getAdminLayoutCssVariables = (token = {}) => ({
  '--admin-color-scheme': getTokenValue(token, 'adminColorScheme', 'light'),
  '--admin-layout-bg': getTokenValue(
    token,
    'adminLayoutBg',
    token.colorBgLayout
  ),
  '--admin-sider-bg': getTokenValue(
    token,
    'adminSiderBg',
    token.colorBgContainer
  ),
  '--admin-sider-border': getTokenValue(
    token,
    'adminSiderBorder',
    token.colorBorderSecondary
  ),
  '--admin-sider-shadow': getTokenValue(
    token,
    'adminSiderShadow',
    token.boxShadowSecondary
  ),
  '--admin-header-bg': getTokenValue(
    token,
    'adminHeaderBg',
    token.colorBgContainer
  ),
  '--admin-header-border': getTokenValue(
    token,
    'adminHeaderBorder',
    token.colorBorderSecondary
  ),
  '--admin-text-color': getTokenValue(token, 'adminTextColor', token.colorText),
  '--admin-text-secondary-color': getTokenValue(
    token,
    'adminTextSecondaryColor',
    token.colorTextSecondary
  ),
  '--admin-primary-color': getTokenValue(
    token,
    'adminPrimaryColor',
    token.colorPrimary
  ),
  '--admin-primary-soft-bg': getTokenValue(
    token,
    'adminPrimarySoftBg',
    token.colorPrimaryBg
  ),
  '--admin-primary-soft-bg-alt': getTokenValue(
    token,
    'adminPrimarySoftBgAlt',
    token.colorPrimaryBgHover
  ),
  '--admin-item-text-color': getTokenValue(
    token,
    'adminItemTextColor',
    token.colorText
  ),
  '--admin-item-hover-bg': getTokenValue(
    token,
    'adminItemHoverBg',
    token.colorFillTertiary
  ),
  '--admin-item-border-color': getTokenValue(
    token,
    'adminItemBorderColor',
    token.colorBorderSecondary
  ),
  '--admin-dropdown-bg': getTokenValue(
    token,
    'adminDropdownBg',
    token.colorBgElevated
  ),
  '--admin-dropdown-item-hover-bg': getTokenValue(
    token,
    'adminDropdownItemHoverBg',
    token.controlItemBgHover
  ),
  '--admin-dropdown-shadow': getTokenValue(
    token,
    'adminDropdownShadow',
    token.boxShadow
  ),
  '--admin-upload-placeholder-bg': getTokenValue(
    token,
    'adminUploadPlaceholderBg',
    token.colorFillQuaternary
  ),
  '--admin-upload-placeholder-border': getTokenValue(
    token,
    'adminUploadPlaceholderBorder',
    token.colorBorderSecondary
  ),
  '--admin-upload-placeholder-icon': getTokenValue(
    token,
    'adminUploadPlaceholderIcon',
    token.colorTextTertiary
  ),
});
